import React, { useState } from "react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { Check, X, ArrowRight, ChevronDown, Star, Clock, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/tchatix/Navbar";
import Footer from "@/components/tchatix/Footer";
import Seo, { breadcrumbLd, faqLd } from "@/components/Seo";
import { Image } from "@/components/ui/image";
import ForfaitInclus from "@/components/tchatix/ForfaitInclus";
import HomeReviews from "@/components/tchatix/HomeReviews";

const MASCOT = "https://media.base44.com/images/public/user_6a69b3e27e902a86c25c35ae/e5009a001_Tchatix20.png";
const TCHATIX_LOGO = "https://media.base44.com/images/public/6a69bb99139c43be5222264c/db430a032_Tchatix.png";
const CALENDLY_URL = "https://calendly.com/tchatixdemo/audit?background_color=001d31&text_color=ffffff&primary_color=0985d8";

// ── Plans (données visuelles uniquement — le texte vient de l'i18n) ────────────
const PLAN_ORDER = ["saas", "creation", "partnership"];
const PLAN_VISUAL = {
  saas: { id: "saas", kind: "base", color: "#00D2FF", glow: "rgba(0,210,255,0.3)", popular: false, requires: false, track: false, ctaHref: "https://calendly.com/tchatixdemo/audit" },
  creation: { id: "creation", kind: "option", color: "#00E676", glow: "rgba(0,230,118,0.3)", popular: true, requires: true, track: false, ctaHref: "https://calendly.com/tchatixdemo/audit" },
  partnership: { id: "partnership", kind: "partner", color: "#C084FC", glow: "rgba(192,132,252,0.3)", popular: false, requires: false, track: true, ctaHref: "https://calendly.com/tchatixdemo/audit" }
};

// ── Comparatif fournisseurs (logos + couleurs — texte via i18n) ────────────────
const COMPARE_PROVIDERS = [
{
  id: "providers",
  logos: [
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/969b4fc72_1702059841openai-icon-png1.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/6f4d36b32_images1.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/c0bfa676e_mistral-color.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/514ed16e7_deepseek-color.png"],

  color: "#405266",
  popular: false
},
{
  id: "generiques",
  logos: [
  "/images/logos/limova.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/e0f99b52e_6551068691020610f4c31c61_Sintra_AI.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/2d5c6f8b0_agentova_ai_logo.jpg"],

  color: "#7B68EE",
  popular: false
},
{
  id: "agences",
  logos: [
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/30c8f4322_zapier_thumb.png",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/c3d9877c5_icon.jpeg",
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/bfe73aec3_n8n.png"],

  color: "#FF5A3C",
  popular: false
},
{
  id: "tchatix",
  logos: ["https://media.base44.com/images/public/6a69bb99139c43be5222264c/db430a032_Tchatix.png"],
  color: "#0985D8",
  popular: true
}];


const COMPARE_ROWS = ["cuisine", "besoin", "agents", "Auto", "liberté", "sécurité", "support", "prix", "conseil"];


function ComparatifFournisseurs({ t }) {
  const isTchatix = (p) => p.id === "tchatix";

  return (
    <div>
      {/* ── Desktop : tableau classique ── */}
      <div className="hidden lg:block tx-glass rounded-3xl overflow-hidden border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#001D31]/8">
              <th className="text-left px-6 py-5 text-xs uppercase tracking-wider text-[#8896B0] font-medium">{t("tarifs.compareCriterion")}</th>
              {COMPARE_PROVIDERS.map((p) =>
              <th
                key={p.id}
                className={`px-4 py-5 text-center font-semibold ${isTchatix(p) ? "bg-[#0985D8]/5" : ""}`}>
                
                  <div className="flex items-center justify-center h-9 mb-2">
                    {p.logos.map((logo, li) =>
                  <div
                    key={li}
                    className={`w-8 h-8 rounded-xl bg-white p-0.5 flex items-center justify-center shadow-sm border border-white/10 ${li > 0 ? "-ml-2.5" : ""}`}
                    style={{ zIndex: p.logos.length - li }}>
                    
                        <Image src={logo} alt="" fittingType="fit" className="w-full h-full object-contain" />
                      </div>
                  )}
                  </div>
                  <span
                  className="flex items-center justify-center gap-1.5 text-sm"
                  style={{ color: isTchatix(p) ? p.color : "#001D31" }}>
                  
                    {t(`tarifs.compareProviders.${p.id}.name`)}
                    {isTchatix(p) &&
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: p.color + "15", color: p.color }}>
                        ✓
                      </span>
                  }
                  </span>
                  <span className="block text-[9px] text-[#8896B0] font-normal mt-0.5">{t(`tarifs.compareProviders.${p.id}.sub`)}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((rowKey, i) =>
            <tr key={rowKey} className={`border-b border-[#001D31]/5 ${i % 2 ? "bg-white/30" : ""}`}>
                <td className="px-6 py-4 text-[13px] font-medium text-[#001D31]">{t(`tarifs.compareRows.${rowKey}`)}</td>
                {COMPARE_PROVIDERS.map((p) => {
                const val = t(`tarifs.compareValues.${p.id}.${rowKey}`);
                return (
                  <td
                    key={p.id}
                    className={`px-4 py-4 text-center align-middle ${isTchatix(p) ? "bg-[#0985D8]/5" : ""}`}>
                    
                      <span
                      className={`text-[12.5px] leading-snug ${
                      isTchatix(p) ?
                      "text-[#0985D8] font-medium" :
                      "text-[#8896B0]"}`
                      }>
                      
                        {val}
                      </span>
                    </td>);

              })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile + tablette : cartes par fournisseur ── */}
      <div className="lg:hidden flex flex-col gap-5">
        {COMPARE_PROVIDERS.map((p, i) =>
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.06 }}
          className={`rounded-2xl overflow-hidden ${isTchatix(p) ? "border-2" : "border"} bg-white`}
          style={{
            borderColor: isTchatix(p) ?
            p.color + "55" :
            "rgba(0,29,49,0.08)"
          }}>
          
            {/* En-tête */}
            <div
            className="flex items-center gap-3 px-5 py-4"
            style={{
              background: isTchatix(p) ?
              p.color + "08" :
              "rgba(248,250,252,0.6)"
            }}>
            
              <div className="flex items-center shrink-0 mr-1">
                {p.logos.map((logo, li) =>
              <div
                key={li}
                className={`w-9 h-9 rounded-xl bg-white p-0.5 flex items-center justify-center shadow-sm border border-white/10 ${
                li > 0 ? "-ml-2.5" : ""}`
                }
                style={{ zIndex: p.logos.length - li }}>
                
                    <img src={logo} alt="" className="w-full h-full object-contain" />
                  </div>
              )}
              </div>
              <div className="min-w-0">
                <span
                className="font-semibold text-sm"
                style={{ color: isTchatix(p) ? p.color : "#001D31" }}>
                
                  {t(`tarifs.compareProviders.${p.id}.name`)}
                </span>
                <span className="block text-[10px] text-[#8896B0] truncate">{t(`tarifs.compareProviders.${p.id}.sub`)}</span>
              </div>
              {isTchatix(p) &&
            <span
              className="ml-auto text-[10px] px-2.5 py-1 rounded-full font-medium shrink-0"
              style={{ background: p.color, color: "#fff" }}>
              
                  {t("tarifs.recommended")}
                </span>
            }
            </div>

            {/* Lignes critères */}
            <div className="divide-y divide-[#001D31]/5">
              {COMPARE_ROWS.map((rowKey) => {
              const val = t(`tarifs.compareValues.${p.id}.${rowKey}`);
              return (
                <div
                  key={rowKey}
                  className="flex items-start justify-between gap-3 px-5 py-3.5">
                  
                    <span className="text-xs text-[#8896B0] shrink-0 pt-0.5 w-[35%]">
                      {t(`tarifs.compareRows.${rowKey}`)}
                    </span>
                    <span
                    className={`text-right text-[13px] leading-snug w-[65%] ${
                    isTchatix(p) ?
                    "text-[#0985D8] font-medium" :
                    "text-[#001D31]/70"}`
                    }>
                    
                      {val}
                    </span>
                  </div>);

            })}
            </div>
          </motion.div>
        )}
      </div>
    </div>);

}

export default function Tarifs() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  // Sur mobile, les reveals `whileInView` (translateY 20-30 px animé en JS par
  // framer-motion, hors de portée du `transition: none` CSS) font "sauter" les
  // titres/blocs après leur affichage. `reducedMotion="always"` rend les
  // transforms instantanés et ne garde que le fondu d'opacité.
  const isMobile = useIsMobile();

  const PLANS = PLAN_ORDER.map((id) => {
    const v = PLAN_VISUAL[id];
    return {
      ...v,
      kindLabel: t(`tarifs.plans.${id}.kindLabel`),
      step: t(`tarifs.plans.${id}.step`),
      name: t(`tarifs.plans.${id}.name`),
      tagline: t(`tarifs.plans.${id}.tagline`),
      sub: t(`tarifs.plans.${id}.sub`),
      requiresText: v.requires ? t(`tarifs.plans.${id}.requiresText`) : null,
      cta: t(`tarifs.plans.${id}.cta`),
      features: t(`tarifs.plans.${id}.features`, { returnObjects: true }) || [],
      track: v.track ? t(`tarifs.plans.${id}.track`) : null
    };
  });

  const REVIEWS = t("tarifs.reviews", { returnObjects: true }) || [];
  const FAQ = t("tarifs.faq", { returnObjects: true }) || [];

  return (
    <MotionConfig reducedMotion={isMobile ? "always" : "user"}>
    <div className="relative min-h-screen text-[#001D31] overflow-x-hidden">
      <Seo
          title={t("tarifs.metaTitle")}
          description={t("tarifs.metaDesc")}
          path="/tarifs"
          jsonLd={[
          breadcrumbLd([
          { name: t("tarifs.breadcrumbHome"), path: "/" },
          { name: t("tarifs.breadcrumbTarifs"), path: "/tarifs" }]
          ),
          faqLd(FAQ)]
          } />
        
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative px-6 text-center overflow-hidden pt-24 pb-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-semibold leading-tight mb-6 [font-family:'Playfair_Display',_serif]">
            {t("tarifs.heroTitle1")}<br />
            <span className="tx-gradient-text">{t("tarifs.heroTitle2")}</span>
          </h1>
          <p className="text-[#8896B0] max-w-2xl mx-auto mb-10 text-base whitespace-pre-line">{t("tarifs.heroDesc")}</p>

          <div className="inline-flex items-center rounded-full p-1.5 bg-white border border-[#e5e7eb] shadow-sm">
            <a href="#offres" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: "#0985D8" }}>
              {t("tarifs.heroCtaOffers")} <ArrowRight className="w-4 h-4" />
            </a>
            <a href="/simulateur" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-[#1a1a1a] hover:bg-[#0985D8]/8 transition-colors">
              <Clock className="w-4 h-4" /> {t("tarifs.heroCtaSimulator")}
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── Plans — cartes claires type référence (sans prix) ── */}
      <section id="offres" className="relative px-6 pb-24 pt-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan, i) =>
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className={`relative bg-white rounded-2xl p-8 flex flex-col border ${plan.popular ? "border-[#0985D8]" : "border-[#e5e7eb]"}`}
              style={plan.popular ? { boxShadow: "0 10px 40px rgba(9,133,216,0.12)" } : undefined}>

              {/* Badge Populaire */}
              {plan.popular &&
              <span className="absolute top-5 right-5 text-[11px] font-semibold text-white px-3 py-1 rounded-full" style={{ backgroundColor: "#0985D8" }}>
                  {t("tarifs.popularLabel")}
                </span>
              }

              <h2 className="text-2xl font-bold text-black [font-family:'Poppins',_sans-serif] pr-20">{plan.name}</h2>
              <p className="text-sm text-[#666] mt-2 mb-6 leading-relaxed">{plan.sub}</p>

              <div className="border-t border-[#e5e7eb] mb-6" />

              <p className="text-xs font-bold text-black mb-4 uppercase tracking-wide">{t("tarifs.featuresHeader")}</p>
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {plan.features.map((f, j) =>
                <li key={j} className="flex items-start gap-2.5 text-sm text-[#001D31]">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#0985D8" }} />
                    <span className="leading-snug">{f}</span>
                  </li>
                )}
              </ul>

              <button
                onClick={() => setDemoOpen(true)}
                className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-transform hover:scale-[1.02] cursor-pointer"
                style={{ backgroundColor: "#222222" }}>
                {plan.cta}
              </button>
            </motion.div>
            )}
        </div>
      </section>

      {/* ── Écriteau : gain de temps moyen d'un agent IA — fond bleu rayé ── */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: "#0985D8" }} />
        <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,1) 0px, rgba(255,255,255,1) 10px, transparent 10px, transparent 60px)"
            }} />
          

        <div className="relative z-10 py-16 md:py-20 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10 md:gap-8 items-center">
            {/* Titre à gauche */}
            <div className="text-left">
              <h2 className="text-white text-4xl md:text-5xl font-bold leading-tight [font-family:'Playfair_Display',_serif]">
                {t("tarifs.gainTitle")}
              </h2>
            </div>

            {/* Description au milieu */}
            <div className="text-center md:text-left">
              <p className="text-base md:text-lg text-white/85 [font-family:'Poppins',_sans-serif] font-light leading-relaxed">
                {t("tarifs.gainSubtitle")}
              </p>
            </div>

            {/* CTA à droite */}
            <div className="flex md:justify-end">
              <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-transform hover:scale-[1.03]"
                  style={{ backgroundColor: "#ffffff", color: "#0985D8" }}>
                {t("tarifs.gainCta")} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>


      <ForfaitInclus />

      {/* ── Comparatif fournisseurs (modèles IA) ── */}
      <section className="relative py-24 px-6 border-t border-white/5 bg-[#F8FAFC]/40">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-semibold leading-tight mb-6 [font-family:'Playfair_Display',_serif]">
              {t("tarifs.compareTitle1")} <br />
              <span className="tx-gradient-text">{t("tarifs.compareTitle2")}</span>
            </h2>
            <p className="text-[#8896B0] max-w-2xl mx-auto mb-8">
              {t("tarifs.compareDesc")}
            </p>
          </motion.div>

          <ComparatifFournisseurs t={t} />
        </div>
      </section>

      {/* ── Avis Google (marquee infini, identique à la home) ── */}
      <HomeReviews showBadge={false} />

      {/* ── FAQ ── */}
      <section className="relative py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* HEADER */}
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14">
            <h2 className="text-3xl md:text-5xl font-semibold leading-tight mb-4 [font-family:'Playfair_Display',_serif]">
              {t("tarifs.faqTitle1")} <span className="tx-gradient-text">{t("tarifs.faqTitle2")}</span>
            </h2>
            <p className="max-w-xl mx-auto text-base text-[#8896B0]">
              {t("tarifs.faqDesc")}
            </p>
          </motion.div>

          {/* CONVERSATION */}
          <div className="flex flex-col gap-6">
            {FAQ.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="flex flex-col">

                  {/* BULLE QUESTION (droite) */}
                  <div className="flex justify-end">
                    <motion.button
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        className="max-w-[88%] md:max-w-[78%] text-left"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}>
                      <div className="relative px-5 py-3.5 rounded-2xl"
                        style={{
                          background: "linear-gradient(135deg, #001D31 0%, #0F3D5C 100%)",
                          boxShadow: "0 4px 24px rgba(0,29,49,0.15), inset 0 1px 0 rgba(255,255,255,0.06)"
                        }}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-white leading-snug flex-1 text-left">{item.q}</span>
                          <motion.div
                              animate={{ rotate: isOpen ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                              className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                              style={{ background: "rgba(252,227,0,0.12)" }}>
                            <ChevronDown className="w-3.5 h-3.5 text-[#FCE300]" />
                          </motion.div>
                        </div>
                      </div>
                    </motion.button>
                  </div>

                  {/* BULLE RÉPONSE (gauche) */}
                  <AnimatePresence>
                    {isOpen &&
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden flex justify-start mt-3">
                        <div className="max-w-[88%] md:max-w-[78%]">
                          <div className="relative px-5 py-4 rounded-2xl"
                          style={{
                            background: "linear-gradient(135deg, rgba(9,133,216,0.06) 0%, rgba(9,133,216,0.02) 100%)",
                            border: "1px solid rgba(9,133,216,0.12)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            boxShadow: "0 2px 12px rgba(9,133,216,0.04), inset 0 1px 0 rgba(255,255,255,0.50)"
                          }}>
                            {/* En-tête avec logo Tchatix */}
                            <div className="flex items-center gap-2.5 mb-3">
                              <div className="w-7 h-7 rounded-full bg-white border border-[#0985D8]/15 flex items-center justify-center p-0.5 shrink-0 shadow-sm">
                                <img
                                  src={TCHATIX_LOGO}
                                  alt="Tchatix"
                                  draggable={false}
                                  className="w-full h-full object-contain rounded-full" />
                              </div>
                              <span className="text-[11px] font-semibold text-[#0985D8]">{t("tarifs.faqTchatix")}</span>
                              <span className="text-[10px] text-[#8896B0]">·</span>
                              <span className="text-[10px] text-[#8896B0]">{t("tarifs.faqInstant")}</span>
                            </div>

                            {/* Texte de la réponse */}
                            <p className="text-sm text-[#405266] leading-relaxed [font-family:'Poppins',_sans-serif]">
                              {item.a}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                      }
                  </AnimatePresence>
                </motion.div>);

              })}
          </div>

          {/* ── BARRE DE CHAT (footer) ── */}
          <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-14">
            <div className="max-w-2xl mx-auto">
              <button
                  onClick={() => setDemoOpen(true)}
                  className="w-full text-left relative rounded-2xl overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.01]"
                  style={{
                    background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.82) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.60)",
                    boxShadow: "0 8px 40px rgba(0,29,49,0.06), 0 1px 3px rgba(0,29,49,0.04), inset 0 1px 0 rgba(255,255,255,0.80)"
                  }}>
                {/* Barre de saisie */}
                <div className="flex items-center gap-3 px-5 py-4">
                  <div className="w-9 h-9 rounded-full bg-white border border-[#0985D8]/20 flex items-center justify-center p-1 shrink-0 shadow-sm">
                    <img
                        src={TCHATIX_LOGO}
                        alt="Tchatix"
                        draggable={false}
                        className="w-full h-full object-contain rounded-full" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#8896B0] [font-family:'Poppins',_sans-serif]">{t("home.faq.anotherQuestion")}</p>
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#0985D8]/8 border border-[#0985D8]/15 text-[#0985D8]">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, #001D31 0%, #0F3D5C 100%)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </div>
                </div>
                {/* LLM sélectionné */}
                <div className="flex items-center justify-between px-5 py-2.5 border-t border-[#001D31]/5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex w-2 h-2">
                        <span className="animate-ping absolute inset-0 rounded-full bg-[#3CBB7A] opacity-40" />
                        <span className="relative rounded-full w-2 h-2 bg-[#3CBB7A]" />
                      </span>
                      <span className="text-[10px] text-[#8896B0] font-mono">{t("home.faq.poweredBy")}</span>
                    </div>
                    <span className="text-[10px] font-semibold text-[#001D31] font-mono tracking-tight px-2 py-0.5 rounded"
                      style={{ background: "linear-gradient(135deg, rgba(9,133,216,0.08), rgba(9,133,216,0.03))", border: "1px solid rgba(9,133,216,0.15)" }}>
                      {t("home.faq.model")}
                    </span>
                    <span className="text-[10px] text-[#8896B0] font-mono">{t("home.faq.modelMeta")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-[#8896B0]" />
                    <span className="w-1 h-1 rounded-full bg-[#8896B0]" />
                    <span className="w-1 h-1 rounded-full bg-[#8896B0]" />
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA final — design plein largeur bleu + liserés blancs ── */}
      <section className="relative w-full overflow-hidden">
        {/* Fond bleu Tchatix plein largeur */}
        <div
            className="absolute inset-0"
            style={{ backgroundColor: "#0985D8" }} />
          
        {/* Texture rayée verticale — clin d'œil au logo */}
        <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,1) 0px, rgba(255,255,255,1) 10px, transparent 10px, transparent 60px)"
            }} />
          
        {/* Grain fin pour la matière */}
        <div
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
            style={{
              backgroundImage:
              "radial-gradient(rgba(255,255,255,0.6) 0.5px, transparent 0.5px)",
              backgroundSize: "3px 3px"
            }} />
          

        {/* Contenu épuré, centré, plein largeur */}
        <div className="relative z-10 py-20 md:py-28 px-6 text-center">
          <h2 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] tracking-tight max-w-4xl mx-auto [font-family:'Poppins',_sans-serif] my-1 px-1 py-1">
            <span className="block">{t("tarifs.ctaTitle1")}</span>
            <span className="block mt-3">{t("tarifs.ctaTitle2")}</span>
          </h2>

          <p className="mt-6 text-white/70 text-base md:text-lg font-light max-w-xl mx-auto [font-family:'Poppins',_sans-serif]">
            {t("tarifs.ctaDesc")}
          </p>

          <div className="mt-12 flex flex-col items-center">
            <button
                onClick={() => setDemoOpen(true)}
                className="group/cta inline-flex items-center gap-3 cursor-pointer">
                
              <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center text-[#0985D8] group-hover/cta:scale-110 transition-transform duration-300">
                <ArrowRight className="w-5 h-5" />
              </span>
              <span className="relative text-white text-xl md:text-2xl font-bold tracking-tight [font-family:'Poppins',_sans-serif]">
                {t("tarifs.ctaBtn")}
                <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-white rounded-full" />
              </span>
            </button>
          </div>

          <div className="flex items-center justify-center gap-4 mt-10 text-[11px] text-white/70 tracking-wide">
            <span className="flex items-center gap-1.5">
              <Shield className="w-3 h-3" />
              {t("tarifs.metaAudit")}
            </span>
            <span className="w-px h-3 bg-white/30" />
            <span>{t("tarifs.metaDuration")}</span>
            <span className="w-px h-3 bg-white/30" />
            <span>{t("tarifs.metaAccompanied")}</span>
          </div>
        </div>
      </section>

      <Footer />

      {/* ── Démo pop-up (Calendly) ── */}
      <AnimatePresence>
        {demoOpen &&
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
            onClick={() => setDemoOpen(false)}>
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 8 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl bg-white rounded-3xl overflow-hidden shadow-2xl">
              <button
                onClick={() => setDemoOpen(false)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-[#001D31]/10 flex items-center justify-center text-[#001D31] transition-colors"
                aria-label={t("tarifs.closeDemo")}>
                <X className="w-5 h-5" />
              </button>
              <iframe
                src={CALENDLY_URL}
                className="w-full"
                style={{ height: "660px", border: "0" }} />
            </motion.div>
          </motion.div>
          }
      </AnimatePresence>
    </div>
    </MotionConfig>);

}